/**
 * "What is this for?" next to the fields people fill in most: short, plain,
 * and true to what Helm does with the value. The forms show them behind an
 * info icon; the guided tours say the same things in their own words.
 */
export const FIELD_HELP = {
  // Inquiries
  inquirySource:
    'The channel the request came in by. It stays on the inquiry, so the team can see where work comes from.',
  sourceScreenshot:
    "A screenshot or PDF of the client's message. Fill in from the source reads it and fills in the next steps for you to check. The file is kept with the inquiry.",
  customerIdentity:
    'Existing customer when you know exactly who it is. Identity not confirmed creates a provisional customer with its own customer ID. It can be matched to the real customer later; nothing is merged automatically.',
  services:
    'What Xtellar does for the client: finding the product (sourcing), shipping, PVoC testing (the conformity check Uganda requires before regulated goods ship), customs clearance, or all of it end to end.',
  reachBy: 'How the client hears from Helm about this request: email, WhatsApp, both, or neither.',
  priority: 'How soon it needs the team, compared with everything else in the queue.',
  hsCode:
    "The Harmonised System code: the customs classification of a product. Uganda uses the East African Community's 8-digit codes, and the code sets the import duty rate. Leave it empty if you're unsure.",
  unitPrice:
    'The price of one unit. Quantity × unit price is the value of the goods, which duty is worked out from.',
  currency: 'The three-letter code of the currency the price is in, such as USD or CNY.',
  sourcingUrl:
    'A link to the product listing (1688, Alibaba, Taobao…), so the sourcing team can find the same item.',
  budget:
    'What the client says they can spend in all, in USD. Supplier quotes are checked against it.',
  declaredValue:
    'The value of the goods as it will be declared to customs, in USD. Duty is worked out on the CIF value: this plus insurance and freight.',
  shipmentMode:
    'How the goods travel. Air is fastest and is charged by chargeable weight; sea suits heavy or bulky cargo; road covers overland legs. For air, say how it flies: freight on scheduled flights, express, courier, or a charter of a whole aircraft.',
  airService:
    'How an air shipment flies. Air freight shares space on scheduled flights; express is the fastest scheduled service; courier is door to door. A charter books a whole aircraft, or several, for this cargo alone: for loads too big, urgent or sensitive for scheduled flights, or for airports few cargo flights reach.',
  incoterms:
    "The Incoterm says where the supplier's job ends and the buyer's begins: who pays freight and insurance, and who carries the risk. EXW: collected at the factory. FOB: loaded on the ship by the supplier. CIF: the supplier also pays sea freight and insurance. DDP: delivered with duties paid.",
  origin: 'Where the goods leave from. For air and sea, choose the airport or port.',
  destination: 'Where the goods are going. For air and sea, choose the arrival airport or port.',
  inquiryStatus:
    "Submitted puts the inquiry in the team's queue now. Draft keeps it internal while you finish capturing it.",
  nextAction:
    'The next thing someone has to do. It shows on the inquiry and in the Inquiries list, so nothing stalls.',
  replyDue:
    'When the client should hear back. The Attention page counts down to it in Kampala working hours.',
  urgency: "The client's reason it can't wait, in their words, such as a shipment cut-off.",

  // Quotes
  quoteStatus:
    "Where this supplier's quote stands: not asked yet (Prospect), Quote requested, Quote received, Proforma uploaded, Customer approved, or Rejected. Quotes still requested show as waiting on the supplier.",
  quotedAmount: "The supplier's price for the order, in the currency chosen next to it.",
  proforma:
    "The supplier's proforma invoice or quotation: paste a link, or upload the file below. It is filed with the inquiry.",
  inquiryPhase:
    'Where the inquiry is: Captured, Requirements, Supplier quotes, Execution, Completed. Only the phases that make sense next can be chosen.',
  operationalState:
    'Active while work goes on, Blocked when it waits on something outside the team, then Completed or Cancelled.',
  nextTask: 'The one next thing to do. Give it a due date and it shows on the Attention page.',

  // Consignments
  consignmentStage:
    'Where the deal stands commercially, from new inquiry to quoted, active and closed. It is separate from where the goods are.',
  shippingModeConsignment:
    "How the goods travel: air, sea or truck. It can't be changed once the consignment code is assigned.",
  trackingNumber:
    "The carrier's reference: a bill of lading (B/L) for sea, an air waybill (AWB) for air, or a container number. Helm tracks the shipment with it.",
  expectedArrival: 'When the goods are due at the destination.',
  insurance:
    'Whether the cargo is insured. The tax estimate then counts 2% of the invoice value as insurance in the CIF value.',
  consignmentHsCode:
    'The customs classification that sets the duty rate. Classify suggests one from the description and photo; check it before relying on it.',
  taxEstimate:
    'Duty is worked out on the CIF value (cost, insurance and freight) in Uganda shillings: import duty first, then excise and VAT on top, plus withholding tax and the infrastructure levy.',
  consignmentPhase:
    'Where the work is: handoff, preparation, in transit, clearance, delivery, completed. Only the phases that make sense next are offered.',
  consignmentStatus:
    'Where the shipment and its paperwork stand, from draft through payment, transit, arrival and clearing to delivered.',
  paymentStatus: "The customer's payment for this consignment: pending, invoiced or paid.",

  // Warehouse
  parcelWeight: 'What the parcel weighs on the scale, in kilograms.',
  parcelDimensions:
    'Length, width and height in cm. Helm works out the volume weight (L × W × H ÷ 6000) and charges whichever is greater, the actual or the volume weight: the chargeable weight.',
  shipWeek: 'The week the parcel is booked to fly out of Guangzhou.',
  claimCode: "The customer code (XTL…) on the parcel's label. It links the parcel to its owner.",
} as const

export type FieldHelpKey = keyof typeof FIELD_HELP
